import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FollowUpType } from 'app/core/models/conclusion/follow-up-type.enum';
import { TreatmentType } from 'app/core/models/conclusion/treatment-type.enum';
import { CurrentUserService } from 'app/core/services/auth/current-user.service';
import { ConclusionService } from 'app/core/services/conclusion/conclusion.service';
import { WoundService } from 'app/core/services/wound/wound.service';
import { PermissionService } from 'app/core/services/user/permission.service';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { ToastService } from 'app/core/services/toast.service';

@Component({
  selector: 'app-conclusion',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    SelectComponent,
  ],
  templateUrl: './conclusion.component.html',
  styleUrl: './conclusion.component.scss',
})
export class ConclusionComponent {
  conclusionForm!: FormGroup;
  isReadOnly: boolean = false;

  @Input() patientId: string;
  @Input() woundId: string;
  @Input() isModal: boolean = false;
  @Input() isConclusionExist: boolean = false;
  @Output() close = new EventEmitter<void>();

  constructor(
    private fb: FormBuilder,
    private conclusionService: ConclusionService,
    private router: Router,
    private woundService: WoundService,
    private currentUserService: CurrentUserService,
    private permissionService: PermissionService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  followUpOptions = Object.keys(FollowUpType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      return {
        label: key.replace(/([A-Z])/g, ' $1').trim(),
        value: FollowUpType[key as keyof typeof FollowUpType],
      };
    });

  treatmentTypes = TreatmentType;

  readonly treatmentKeys = [
    'debridement',
    'antibiotic',
    'cream',
    'solution',
    'other',
  ];

  @ViewChild('procedureList') procedureList!: ElementRef<HTMLDivElement>;

  ngOnInit(): void {
    this.conclusionForm = this.fb.group({
      followUp: [null, Validators.required],
      procedures: this.fb.array([]),
      treatments: this.fb.group({}),
      nurseTreatments: this.fb.group({}),
      nurseNote: [''],
    });

    this.initTreatments();
    this.initNurseTreatments();
    this.addProcedure();

    this.checkWoundDoctor();
    if (this.isConclusionExist) {
      this.loadConclusionDetails();
    }
  }

  loadConclusionDetails() {
    this.conclusionService
      .getConclusionDetailByWoundId(this.woundId)
      .subscribe((res) => {
        if (res.data) {
          this.isReadOnly = true;
          this.conclusionForm.disable();
          
          if (this.canCompleteConclusion) {
            this.conclusionForm.get('nurseNote')?.enable();
            this.conclusionForm.get('nurseTreatments')?.enable();
          }
          
          const data = res.data;
          this.conclusionForm.patchValue({
            followUp: data.followUp,
          });

          if (data.procedures && data.procedures.length > 0) {
            const procedures = this.conclusionForm.get(
              'procedures'
            ) as FormArray;
            procedures.clear();
            data.procedures.forEach((p: any) => {
              procedures.push(
                this.fb.control(p.description, Validators.required)
              );
            });
          }

          if (data.treatments) {
            data.treatments.forEach((t: any) => {
              const key = this.getTreatmentKey(t.treatmentType);
              const group = this.treatmentsGroup.get(key);
              if (group) {
                group.patchValue({
                  selected: true,
                  details: t.details,
                });
              }
            });
          }
        }
      });
  }

  private getTreatmentKey(type: TreatmentType): string {
    switch (type) {
      case TreatmentType.Debridement:
        return 'debridement';
      case TreatmentType.Antibiotic:
        return 'antibiotic';
      case TreatmentType.Cream:
        return 'cream';
      case TreatmentType.Solution:
        return 'solution';
      case TreatmentType.Other:
        return 'other';
      default:
        return 'other';
    }
  }

  checkWoundDoctor() {
    this.woundService.getWoundById(this.woundId).subscribe((res) => {
      const currentUserId = this.currentUserService.GetSession().id;
      if (res.data.doctorId !== currentUserId) {
        this.isReadOnly = true;
        this.conclusionForm.disable();
        
        if (this.canCompleteConclusion) {
          this.conclusionForm.get('nurseNote')?.enable();
          this.conclusionForm.get('nurseTreatments')?.enable();
        }
      }
    });
  }

  get procedures(): FormArray {
    return this.conclusionForm.get('procedures') as FormArray;
  }

  addProcedure(): void {
    this.procedures.push(this.fb.control('', Validators.required));

    setTimeout(() => {
      const el = this.procedureList.nativeElement;
      el.scrollTop = el.scrollHeight;
    });
  }

  removeProcedure(index: number): void {
    if (this.procedures.length > 1) {
      this.procedures.removeAt(index);
    }
  }

  get treatmentsGroup(): FormGroup {
    return this.conclusionForm.get('treatments') as FormGroup;
  }

  createTreatmentGroup(): FormGroup {
    return this.fb.group({
      selected: [false],
      details: [''],
    });
  }

  treatmentSelected(key: string): boolean {
    return this.treatmentsGroup.get(key)?.get('selected')?.value;
  }

  private initTreatments(): void {
    this.treatmentKeys.forEach((key) => {
      this.treatmentsGroup.addControl(
        key,
        this.fb.group({
          selected: [false],
          details: [''],
        })
      );

      const group = this.treatmentsGroup.get(key) as FormGroup;

      group.get('selected')!.valueChanges.subscribe((selected) => {
        const details = group.get('details');
        if (selected) {
          details?.setValidators([Validators.required]);
        } else {
          details?.clearValidators();
          details?.setValue('');
        }
        details?.updateValueAndValidity();
      });
    });
  }

  private initNurseTreatments(): void {
    const nurseTreatmentsGroup = this.conclusionForm.get('nurseTreatments') as FormGroup;
    this.treatmentKeys.forEach((key) => {
      nurseTreatmentsGroup.addControl(
        key,
        this.fb.group({
          completed: [false],
        })
      );
    });
  }



  hasDoctorTreatment(key: string): boolean {
    const group = this.treatmentsGroup.get(key);
    return group?.get('selected')?.value && !!group?.get('details')?.value;
  }

  getDoctorTreatmentDetails(key: string): string {
    return this.treatmentsGroup.get(key)?.get('details')?.value || '';
  }

  private areAllTreatmentsCompleted(): boolean {
    const nurseTreatmentsGroup = this.conclusionForm.get('nurseTreatments') as FormGroup;
    
    const prescribedTreatments = this.treatmentKeys.filter(key => this.hasDoctorTreatment(key));
    
    return prescribedTreatments.every(key => {
      return nurseTreatmentsGroup.get(key)?.get('completed')?.value === true;
    });
  }

  saveConclusion(): void {
    if (this.conclusionForm.invalid) {
      this.conclusionForm.markAllAsTouched();
      return;
    }

    const payload = {
      patientId: this.patientId,
      woundId: this.woundId,
      followUp: this.conclusionForm.value.followUp,
      procedures: this.procedures.value.filter((x: string) => !!x),
      treatments: this.mapTreatments(),
    };

    this.conclusionService
      .createConclusion({
        followUp: Number(this.conclusionForm.value.followUp),
        patientId: payload.patientId,
        procedures: payload.procedures,
        treatments: payload.treatments,
        woundId: payload.woundId,
      })
      .subscribe({
        next: () => {
          this.router.navigateByUrl('/work-list/worklist');
        },
      });
  }

  completeConclusion(): void {
    if (!this.areAllTreatmentsCompleted()) {
      this.toastService.showToast(
        this.translate.instant('conclusionPage.completionWarning'),
        'warning'
      );
      return;
    }

    const nurseNote = this.conclusionForm.get('nurseNote')?.value || '';

    
    this.conclusionService.completeConclusion(
      this.woundId, 
      nurseNote
    ).subscribe(() => {
      if (this.isModal) {
        this.close.emit();
      } else {
        this.router.navigateByUrl('/work-list/worklist');
      }
    });
  }

  get canCompleteConclusion(): boolean {
    return (
      this.permissionService.hasPermission('nurse_claim') ||
      this.permissionService.hasPermission('CompleteConclusionClaim')
    );
  }

  get canSaveConclusion(): boolean {
    return this.permissionService.hasPermission('specialist_claim');
  }

  private mapTreatments(): { treatmentType: TreatmentType; details: string }[] {
    const result: any[] = [];

    this.treatmentKeys.forEach((key) => {
      const group = this.treatmentsGroup.get(key) as FormGroup;
      if (group.get('selected')?.value) {
        result.push({
          treatmentType: this.mapTreatmentType(key),
          details: group.get('details')?.value,
        });
      }
    });

    return result;
  }

  private mapTreatmentType(key: string): TreatmentType {
    switch (key) {
      case 'debridement':
        return TreatmentType.Debridement;
      case 'antibiotic':
        return TreatmentType.Antibiotic;
      case 'cream':
        return TreatmentType.Cream;
      case 'solution':
        return TreatmentType.Solution;
      default:
        return TreatmentType.Other;
    }
  }
}
